import { Box, Divider, Flex, Text } from '@chakra-ui/react'
import { BloodPressure } from '@hb/shared'
import { format } from 'date-fns'
import type { FC, MouseEvent } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Line,
  Tooltip as RechartsTooltip,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartLegend } from './ChartLegend'
import type { LaborChartXAxisProps } from './DilationStationGraph'

// ── Helpers ────────────────────────────────────────────────────────────────

const bpColor = (systolic: number, diastolic: number) => {
  if (systolic >= 160 || diastolic >= 110) return '#E53E3E' // severe hypertension
  if (systolic >= 140 || diastolic >= 90) return '#ED8936' // mild hypertension
  if (systolic < 90 || diastolic < 60) return '#F6AD55' // hypotension
  return '#4299E1'
}

const pulseColor = (bpm: number) =>
  bpm > 120 || bpm < 50 ? '#E53E3E' : bpm > 100 || bpm < 60 ? '#ED8936' : '#9F7AEA'

const tempColor = (tempF: number) =>
  tempF >= 100.4 ? '#E53E3E' : tempF <= 96.8 ? '#63B3ED' : '#48BB78'

const rrColor = (rr: number) =>
  rr > 24 || rr < 10 ? '#E53E3E' : rr > 20 || rr < 12 ? '#ED8936' : '#48BB78'

const safeFormatTime = (ms: number) => {
  try {
    return format(new Date(ms), 'HH:mm')
  } catch {
    return ''
  }
}

// ── Shared Vitals Display ──────────────────────────────────────────────────

export interface VitalsExamDisplayProps {
  temperature?: number
  bloodPressure?: BloodPressure
  pulse?: number
  respiratoryRate?: number
}

/** Full vitals summary — used in both the tooltip and the entry click popover. */
export const VitalsExamDisplay = ({
  temperature,
  bloodPressure,
  pulse,
  respiratoryRate,
}: VitalsExamDisplayProps) => {
  const hasBP = bloodPressure != null
  const bpCol = hasBP ? bpColor(bloodPressure.systolic, bloodPressure.diastolic) : 'gray.500'
  const pulseCol = pulse != null ? pulseColor(pulse) : 'gray.500'
  const tempCol = temperature != null ? tempColor(temperature) : 'gray.500'
  const rrCol = respiratoryRate != null ? rrColor(respiratoryRate) : 'gray.500'

  return (
    <Flex direction="column" gap={0.5}>
      {hasBP ? (
        <Flex justify="space-between" align="center">
          <Text fontSize="xs" color="gray.400">
            Blood Pressure
          </Text>
          <Flex align="baseline" gap={0.5}>
            <Text fontSize="sm" fontWeight={700} color={bpCol}>
              {bloodPressure.systolic}/{bloodPressure.diastolic}
            </Text>
            <Text fontSize="xs" color={bpCol}>
              mmHg
            </Text>
          </Flex>
        </Flex>
      ) : null}
      {pulse != null ? (
        <Flex justify="space-between" align="center">
          <Text fontSize="xs" color="gray.400">
            Pulse
          </Text>
          <Flex align="baseline" gap={0.5}>
            <Text fontSize="sm" fontWeight={700} color={pulseCol}>
              {pulse}
            </Text>
            <Text fontSize="xs" color={pulseCol}>
              bpm
            </Text>
          </Flex>
        </Flex>
      ) : null}
      {(hasBP || pulse != null) && (temperature != null || respiratoryRate != null) ? (
        <Divider my={1} />
      ) : null}
      {temperature != null ? (
        <Flex justify="space-between" align="center">
          <Text fontSize="xs" color="gray.400">
            Temperature
          </Text>
          <Flex align="baseline" gap={0.5}>
            <Text fontSize="sm" fontWeight={700} color={tempCol}>
              {temperature.toFixed(1)}
            </Text>
            <Text fontSize="xs" color={tempCol}>
              °F
            </Text>
          </Flex>
        </Flex>
      ) : null}
      {respiratoryRate != null ? (
        <Flex justify="space-between" align="center">
          <Text fontSize="xs" color="gray.400">
            Resp. Rate
          </Text>
          <Flex align="baseline" gap={0.5}>
            <Text fontSize="sm" fontWeight={700} color={rrCol}>
              {respiratoryRate}
            </Text>
            <Text fontSize="xs" color={rrCol}>
              /min
            </Text>
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  )
}

// ── Legend items ───────────────────────────────────────────────────────────

const VITALS_LEGEND_ITEMS = [
  { kind: 'line' as const, label: 'Systolic BP (mmHg)', color: '#4299E1' },
  { kind: 'line' as const, label: 'Diastolic BP (mmHg)', color: '#90CDF4', dashed: true },
  { kind: 'line' as const, label: 'Pulse (bpm)', color: '#9F7AEA' },
  {
    kind: 'band' as const,
    label: 'Normal BP range (90–140 / 60–90)',
    fill: 'rgba(66,153,225,0.10)',
  },
  {
    kind: 'line' as const,
    label: 'Hypertension threshold (140/90)',
    color: '#FC8181',
    dashed: true,
  },
]

// ── Data + Tooltip ─────────────────────────────────────────────────────────

export interface VitalsPoint {
  time: number
  systolic?: number
  diastolic?: number
  pulse?: number
  temperature?: number
  respiratoryRate?: number
  entryId?: string
}

const VitalTooltip = ({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ payload?: VitalsPoint }>
}) => {
  if (!active || !payload?.length) return null
  const pt = payload[0]?.payload
  if (!pt) return null

  const { time, systolic, diastolic, pulse, temperature, respiratoryRate } = pt
  const hasAny =
    systolic != null ||
    diastolic != null ||
    pulse != null ||
    temperature != null ||
    respiratoryRate != null

  return (
    <Box
      bg="white"
      _dark={{ bg: 'gray.800', borderColor: 'whiteAlpha.300' }}
      border="1px solid"
      borderColor="gray.200"
      borderRadius={8}
      px={3}
      py={2.5}
      shadow="md"
      fontFamily="hero-new"
      fontSize="sm"
      minW="220px"
      maxW="280px">
      {time != null ? (
        <Text fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={1.5}>
          {safeFormatTime(time)}
        </Text>
      ) : null}
      {hasAny ? (
        <>
          <Divider mb={1.5} />
          <VitalsExamDisplay
            bloodPressure={
              systolic != null && diastolic != null ? { systolic, diastolic } : undefined
            }
            pulse={pulse}
            temperature={temperature}
            respiratoryRate={respiratoryRate}
          />
        </>
      ) : null}
    </Box>
  )
}

// ── Graph ──────────────────────────────────────────────────────────────────

export const VitalsGraph: FC<{
  vitalsPoints: VitalsPoint[]
  xAxisProps: LaborChartXAxisProps
  gridColor: string
  tickStyle: { fontFamily: string; fontSize: number; fill: string }
  onChartClick: (data: any, event: MouseEvent) => void
}> = ({ vitalsPoints, xAxisProps, gridColor, tickStyle, onChartClick }) => (
  <>
    <Box w="100%" h="220px" pl={1} pr={4} cursor="pointer">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={vitalsPoints}
          margin={{ top: 8, right: 52, left: 8, bottom: 8 }}
          syncId="labor"
          onClick={onChartClick}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis {...xAxisProps} />
          <YAxis
            domain={[50, 180]}
            ticks={[60, 80, 90, 100, 120, 140, 160, 180]}
            tick={tickStyle}
            label={{
              value: 'mmHg / bpm',
              angle: -90,
              dx: -12,
              position: 'middle',
              style: { fontFamily: 'hero-new', fontSize: 11, fill: '#4299E1' },
            }}
          />

          {/* Normal BP band */}
          <ReferenceArea y1={60} y2={90} fill="rgba(66,153,225,0.07)" stroke="none" />
          <ReferenceArea y1={90} y2={140} fill="rgba(66,153,225,0.07)" stroke="none" />

          {/* Hypertension threshold lines */}
          <ReferenceLine
            y={140}
            stroke="#FC8181"
            strokeDasharray="4 4"
            label={{
              value: '140',
              position: 'right',
              style: { fontSize: 9, fontFamily: 'hero-new', fill: '#C53030' },
            }}
          />
          <ReferenceLine
            y={90}
            stroke="#FC8181"
            strokeDasharray="4 4"
            label={{
              value: '90',
              position: 'right',
              style: { fontSize: 9, fontFamily: 'hero-new', fill: '#C53030' },
            }}
          />

          <RechartsTooltip content={<VitalTooltip />} />

          {/* Diastolic BP */}
          <Line
            dataKey="diastolic"
            name="Diastolic"
            stroke="#90CDF4"
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={{ r: 3.5, fill: '#90CDF4', stroke: '#fff', strokeWidth: 1.5 }}
            activeDot={{ r: 6 }}
            isAnimationActive={false}
            connectNulls
          />

          {/* Systolic BP */}
          <Line
            dataKey="systolic"
            name="Systolic"
            stroke="#4299E1"
            strokeWidth={2}
            dot={{ r: 4, fill: '#4299E1', stroke: '#fff', strokeWidth: 1.5 }}
            activeDot={{ r: 7 }}
            isAnimationActive={false}
            connectNulls
          />

          {/* Pulse */}
          <Line
            dataKey="pulse"
            name="Pulse"
            stroke="#9F7AEA"
            strokeWidth={2}
            dot={{ r: 4, fill: '#9F7AEA', stroke: '#fff', strokeWidth: 1.5 }}
            activeDot={{ r: 7 }}
            isAnimationActive={false}
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
    <ChartLegend items={VITALS_LEGEND_ITEMS} />
  </>
)
